import { createSelector } from '@reduxjs/toolkit';

import { RootState } from 'state/global-state/store';
import { IParsedLoyaltyReward } from 'state/loyalty/hooks/types';

import { computeTotalTimesRewardApplied, getAppliedPointsInCart } from './rewards.utils';

export const selectAppliedLoyaltyRewards = ({ loyalty }: RootState) => {
  return loyalty.rewards.appliedLoyaltyRewards;
};

export const selectAppliedLoyaltyRewardsArray = createSelector(
  [selectAppliedLoyaltyRewards],
  (appliedLoyaltyRewards): IParsedLoyaltyReward[] | null => {
    const entries = Object.entries(appliedLoyaltyRewards);
    if (entries.length === 0) {
      return null;
    }

    const arr = entries
      .map(([cartId, reward]) => ({
        cartId,
        rewardId: reward.rewardId,
        timesApplied: reward.timesApplied,
        sanityId: reward.sanityId,
        redemptionMethod: reward.redemptionMethod,
      }))
      .filter(reward => reward.timesApplied > 0);

    return arr?.length ? arr : null; // matches previous implementation and avoid re-renders on new empty array
  }
);

export const selectAvailableLoyaltyRewardsMap = ({ loyalty }: RootState) => {
  return loyalty.rewards.availableLoyaltyRewardsMap;
};

export const selectIsPricingRewardApplication = ({ loyalty }: RootState) => {
  return loyalty.rewards.isPricingRewardApplication;
};

export const selectStagedCartPoints = ({ loyalty }: RootState) => {
  return loyalty.rewards.stagedCartPoints;
};

export const selectTotalTimesRewardApplied = createSelector(
  [selectAppliedLoyaltyRewards],
  computeTotalTimesRewardApplied
);

export const selectShouldRefetchRewards = ({ loyalty }: RootState) => {
  return loyalty.rewards.shouldRefetchRewards;
};

export const selectAppliedPoints = ({ loyalty }: RootState) =>
  getAppliedPointsInCart(loyalty.rewards);

// This selector also serves as a placeholder, when the Reward Unstacking is fully released
// we can start working on removing all the logic the supports having several applied
// rewards, which won't be the case anymore
export const selectAppliedReward = createSelector(
  selectAppliedLoyaltyRewardsArray,
  rewards => rewards?.[0]
);

export const selectRewardsFeedbackMap = ({ loyalty }: RootState) => {
  return loyalty.rewards.rewardsFeedbackMap;
};
